// @flow
import React, { Fragment } from 'react';
import { useSelector } from 'react-redux';
import { dataLayer } from '@finect/front-gtm';
import { OptionsInner, Bullets, Expand, Price, ButtonContainer, Option, ProfDisclaimer, PromoBadge } from '../partials';
import { getPlusItem } from '../../../../utils/ga4Plus';
import { shouldShowTrialPromo } from '../../../../utils/Plus/trial';
import type { OptionsType } from './data';
import { options, plusOptions } from './data';

type Props = {
  plus?: boolean,
  option: OptionsType,
  url: string,
  prices?: {
    monthly: { price: number, original?: number },
    yearly: { price: number, original: number },
    trial?: { months: ?number, endDate: ?string }
  },
  onMonthlyClick?: () => void
};

export const OptionCard = ({ plus = false, option, url, prices, onMonthlyClick }: Props) => {
  if (plus) {
    const isSegment50103 = useSelector(state => state.profile.segments['50103']);
    const data = plusOptions[option];

    const Logo = data.logo ? data.logo : <Fragment />;

    // si no hay precios, opción free
    if (!prices) {
      return (
        <OptionsInner plus background={data.background} color={data.color} color2={data.color2}>
          {data.logo && <Logo {...data.logoProps} />}

          {data.title.includes('<') ? <h4 dangerouslySetInnerHTML={{ __html: data.title }} /> : <h4>{data.title}</h4>}

          <Bullets fontSize={data.bulletsSize || 1}>
            {data.bullets.map((bullet, index) => (
              <span key={index} className="bullet" dangerouslySetInnerHTML={{ __html: bullet }} />
            ))}
          </Bullets>

          <Expand />

          <div className="price">
            <Price priceFontSize={data.priceFontSize}>{data.price}</Price>
          </div>

          <ButtonContainer background={data.buttonBackgroundColor} color={data.buttonColor}>
            <a href={`${url}${option !== 'free' ? '?plan=monthly' : ''}`} rel="nofollow">
              {data.button}
            </a>
          </ButtonContainer>
        </OptionsInner>
      );
    }

    const monthlyPrice = prices?.monthly?.price || null;
    const monthlyOriginal = prices?.monthly?.original || null;
    const yearlyPrice = prices?.yearly?.price || null;
    const yearlyOriginal = prices?.yearly?.original || null;

    const trialEndDate = prices?.trial?.endDate || null;
    const trialEndDateFormatted = (() => {
      if (!trialEndDate) return '';
      const date = new Date(trialEndDate);
      return `${date.getDate()} de ${date.toLocaleString('es-ES', { month: 'long' })}`;
    })();

    const plusTrialUsed = useSelector((state) => {
      const payments = state.profile.profile.model?.payments;
      if (Array.isArray(payments)) {
        return payments.some(p => p.plusTrialUsed === 1);
      }
      return payments?.plusTrialUsed;
    });
    const showPromoBadge = shouldShowTrialPromo(prices?.trial, plusTrialUsed);

    return (
      <OptionsInner plus background={data.background} color={data.color} color2={data.color2}>
        {data.logo && <Logo {...data.logoProps} />}

        {data.title.includes('<') ? <h4 dangerouslySetInnerHTML={{ __html: data.title }} /> : <h4>{data.title}</h4>}

        <Bullets fontSize={data.bulletsSize || 1}>
          {data.bullets.map((bullet, index) => (
            <span key={index} className="bullet" dangerouslySetInnerHTML={{ __html: bullet }} />
          ))}
        </Bullets>

        <Expand />

        <div className="price">
          {monthlyOriginal && (
            <Price
              priceFontSize={data.priceFontSize ? data.priceFontSize - 3 : undefined}
              strikethrough
              priceFontWeight={200}
            >
              {typeof monthlyOriginal === 'number'
                ? `${monthlyOriginal.toString().replace('.', ',')}€`
                : monthlyOriginal}
              {data.period ? <span> / {data.period}</span> : ''}
            </Price>
          )}

          <Price priceFontSize={data.priceFontSize}>
            {typeof monthlyPrice === 'number' ? `${monthlyPrice.toString().replace('.', ',')}€` : monthlyPrice}
            {data.period ? <span> / {data.period}</span> : ''}
          </Price>
        </div>

        <ButtonContainer background={data.buttonBackgroundColor} color={data.buttonColor}>
          <button
            onClick={(e) => {
              dataLayer({
                id: 'PlusSelectItem',
                type: 'select_item',
                params: {
                  items: [getPlusItem('monthly', monthlyPrice, monthlyOriginal)]
                }
              });

              if (onMonthlyClick) {
                onMonthlyClick();
                return;
              }

              dataLayer({
                id: 'PlusAddToCart',
                type: 'add_to_cart',
                params: {
                  value: monthlyPrice,
                  items: [getPlusItem('monthly', monthlyPrice, monthlyOriginal)]
                }
              });

              window.location.href = `${url}${option !== 'free' ? '?plan=monthly' : ''}`;
            }}
          >
            {data.button}
          </button>
        </ButtonContainer>

        <div className="price" style={{ marginTop: '40px' }}>
          <Price
            priceFontSize={data.priceFontSize ? data.priceFontSize - 3 : undefined}
            strikethrough
            priceFontWeight={200}
          >
            {/* $FlowFixMe */}
            {typeof yearlyOriginal === 'number' ? `${yearlyOriginal.toString().replace('.', ',')}€` : yearlyOriginal}
            {data.priceYearlyPeriod ? <span> / {data.priceYearlyPeriod}</span> : ''}
          </Price>

          <Price priceFontSize={data.priceFontSize}>
            {/* $FlowFixMe */}
            {typeof yearlyPrice === 'number' ? `${yearlyPrice.toString().replace('.', ',')}€` : yearlyPrice}
            {data.priceYearlyPeriod ? <span> / {data.priceYearlyPeriod}</span> : ''}
          </Price>
        </div>

        <ButtonContainer background={data.buttonBackgroundColor2} color={data.buttonColor}>
          <button
            onClick={() => {
              dataLayer({
                id: 'PlusSelectItem',
                type: 'select_item',
                params: {
                  items: [getPlusItem('yearly', yearlyPrice, yearlyOriginal)]
                }
              });
              dataLayer({
                id: 'PlusAddToCart',
                type: 'add_to_cart',
                params: {
                  value: yearlyPrice,
                  items: [getPlusItem('yearly', yearlyPrice, yearlyOriginal)]
                }
              });
              window.location.href = `${url}?plan=yearly`;
            }}
          >
            {data.button}
          </button>
        </ButtonContainer>

        {showPromoBadge && (
          <PromoBadge>
            <span className="asterisk">*</span>
            {isSegment50103 ? (
              <p>
                Completa tu suscripción antes del {trialEndDateFormatted} y obtén tu
                {prices.trial?.months === 1 ? '' : 's'} {prices.trial?.months}{' '}
                {prices.trial?.months === 1 ? 'mes' : 'meses'} GRATIS
              </p>
            ) : (
              <p>
                {prices.trial?.months} {prices.trial?.months === 1 ? 'mes' : 'meses'} GRATIS para suscripciones antes del{' '}
                {trialEndDateFormatted}
              </p>
            )}
          </PromoBadge>
        )}

        <ProfDisclaimer>
          <p>
            Si necesitas contratar más de una cuenta, contacta con nosotros en{' '}
            <a href="mailto:finect@finect.com">finect@finect.com</a>
          </p>
        </ProfDisclaimer>

        {/* <ButtonContainer background={data.buttonBackgroundColor} color={data.buttonColor}>
          <a href={url} rel="nofollow">
            {data.button}
          </a>
        </ButtonContainer> */}
      </OptionsInner>
    );
  }

  const data = options[option];

  return (
    <OptionsInner>
      <h4>{data.title}</h4>

      <span>{data.highlight}</span>

      <div>
        {data.price}
        {data.period ? <span> / {data.period}</span> : ''}
      </div>

      <Option primary as="a" href={url} rel="nofollow">
        {data.button}
      </Option>
    </OptionsInner>
  );
};
